import { HttpLink, InMemoryCache, ApolloClient, ApolloLink } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { getLocalStorageToken } from 'store/localStorage'
import cursorBasedPagination from 'utils/cursorBasedPagination'
import { ErrorLink } from '@apollo/client/link/error'
import { ClientAwarenessLink } from '@apollo/client/link/client-awareness'
import { CombinedGraphQLErrors } from '@apollo/client/errors'
import * as Sentry from '@sentry/browser'
import signOut from 'utils/signOut'
import { RFID_COLLISION } from 'graphql/rfidCollision'

// User-facing error codes deliberately thrown by resolvers. The calling
// component shows a toast/empty-state for these — don't double-report
// to Sentry, where they're pure noise (FLEXIME-AY).
export const SUPPRESSED_ERROR_CODES = new Set(['NO_TIMESTAMPS'])

// Backend GraphQLErrors that have no extensions.code but are still
// user-facing — the UI surfaces a toast/error state for these. T3.9.
// Verified against graphql-api/src messages 2026-06-16.
export const SUPPRESSED_ERROR_MESSAGES: readonly RegExp[] = [
  RFID_COLLISION,
  /^Date range cannot exceed 31 days/,
  /^Could not get ipPerson/,
  /^Could not get ipPersons/,
  // "Credentials wrong." — login mutation rejects bad password; LoginView
  // shows an inline error. The Sentry capture in index.tsx ignoreErrors
  // misses this because T3.7 wraps GraphQL errors with name="GraphQLError",
  // so Sentry titles them "GraphQLError: Credentials wrong." — not "Error: …".
  // Suppress at the errorLink layer instead (FRONTEND-5, 2026-06-17).
  /^Credentials wrong\.?$/,
  // W41 slice 1 (graphql-api, 2026-10-07): removeTerminal's refusals carry stable
  // Swedish messages that RemoveTerminalView shows in its toast. Expected answers,
  // not defects. "No resolved user" is deliberately NOT here: it should never
  // happen and should reach Sentry if it does.
  /^Ange en anledning\. Terminalen har stämplingar/,
  /^Terminalen är en fysisk enhet med stämplingar/,
  /^Terminalen är redan borttagen\.?$/,
  // ... and unretireTerminal's (RestoreTerminalView).
  /^Terminalen är inte borttagen\.?$/,
  /^Ange en anledning till att terminalen återställs/,
  // W63 (graphql-api #456): updateTerminal's check of the break on created shifts; CreateWorkdayBreak shows it.
  /^Ange från hur långa pass rasten ska läggas in/,
  // ... and its refusal while the agency doesn't have the break switched on (graphql-api #467).
  /^Rast på skapade pass är inte påslaget för er/,
  // ... and its refusals of the create-workday order number (graphql-api #484); CreateWorkday shows them (ORDER_REFUSALS).
  /^Ange ett giltigt uppdragsnummer/,
  /^Uppdrag \d+ finns inte i Intelliplan/,
  /^Kunde inte kontrollera uppdraget hos Intelliplan/,
  /^Ange uppdragsnumret: utan uppdrag kan terminalen inte skapa arbetspass/,
  // T4.20 (graphql-api #490): sendFileImport without the acceptance for removing planned passes; FileImportStatusBar shows it.
  /^Importen tar bort \d+ planerade pass i Intelliplan/,
]

// graphql-api's verifyTokenUser, for a valid token whose user's privilegeLevel is below what the field needs.
const PRIVILEGE_TOO_LOW = /^User privilegeLevel is too low/

interface GraphQLLikeError {
  message?: string
  path?: readonly (string | number)[]
  locations?: readonly { line: number; column: number }[]
  extensions?: { code?: unknown } | null
}

interface OperationLike {
  operationName?: string
}

// Apollo 4's GraphQLError instances do not always reach Sentry as proper Error
// objects — Sentry then captures them as "Object captured as exception with
// keys: extensions, locations, message, path" and groups them under minified
// frame names. T3.7. Wrap into a real Error preserving the original payload
// as `extra` so Sentry can title and group by the actual message.
//
// IMPORTANT (2026-06-17): all wrapped errors share name="GraphQLError" and
// originate from the same frame (captureGraphQLError), so Sentry's default
// grouping by (type, first in-app frame) collapses every GraphQL error into
// one issue (saw "Failed getting fileImportMeta…" merged with "Credentials
// wrong" under FRONTEND-5). Pass an explicit fingerprint that includes the
// message + operationName so distinct backend errors stay distinct.
function captureGraphQLError(err: GraphQLLikeError, operationName: string | undefined): void {
  const message = err.message ?? 'GraphQL error (no message)'
  const wrapped = new Error(message)
  wrapped.name = 'GraphQLError'
  Sentry.captureException(wrapped, {
    fingerprint: ['graphql', operationName ?? 'unknown', message],
    extra: {
      operationName,
      message: err.message,
      path: err.path,
      locations: err.locations,
      extensions: err.extensions,
    },
  })
}

// Extracted from the errorLink callback to make it unit-testable. The link
// callback delegates here so we can verify (a) null entries in errors[] are
// skipped without crashing (FLEXIME-AW), (b) UNAUTHENTICATED triggers signOut,
// and (c) suppressed codes/messages don't reach Sentry, and (d) errors that
// do reach Sentry are wrapped in proper Error instances (T3.7).
export function handleCombinedGraphQLErrors(
  errors: ReadonlyArray<GraphQLLikeError | null | undefined>,
  operation: OperationLike,
): void {
  errors.filter(Boolean).forEach((err) => {
    const e = err as GraphQLLikeError
    if (e.extensions?.code === 'UNAUTHENTICATED') {
      // graphql-api refuses a query above the user's level with the same code as a bad token. The token is
      // fine then, so signing out only sent the user back to a login that led to the same page (T4.17).
      // Report it instead: after RequirePrivilegeLevel, it means some page asks for more than its users have.
      if (e.message && PRIVILEGE_TOO_LOW.test(e.message)) {
        captureGraphQLError(e, operation.operationName)
        return
      }
      signOut()
      return
    }
    if (SUPPRESSED_ERROR_CODES.has(String(e.extensions?.code))) return
    if (e.message && SUPPRESSED_ERROR_MESSAGES.some((re) => re.test(e.message!))) return
    captureGraphQLError(e, operation.operationName)
  })
}

const errorLink = new ErrorLink(({ error, operation }) => {
  if (CombinedGraphQLErrors.is(error)) {
    handleCombinedGraphQLErrors(error.errors, operation)
  } else {
    const statusCode = 'statusCode' in error ? (error as Record<string, unknown>).statusCode as number | undefined : undefined
    if (statusCode === 401 || statusCode === 403) {
      signOut()
    } else {
      Sentry.captureException(error, {
        extra: { operationName: operation.operationName, statusCode },
      })
    }
  }
})
const getClient = (url?: string) => {
  const authLink = setContext((_, { headers }) => {
    // get the authentication token from local storage if it exists
    const token = getLocalStorageToken()
    // return the headers to the context so httpLink can read them
    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : '',
      },
    }
  })
  const httpLink = new HttpLink({
    uri: url,
  })
  return new ApolloClient({
    link: ApolloLink.from([errorLink, authLink, new ClientAwarenessLink(), httpLink]),
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            cursorPaginatedTimestamps: cursorBasedPagination(['ipPersonId', 'limit', 'TerminalId']),
            cursorPaginatedDebugData: cursorBasedPagination(['TimestampId', 'limit']),
          },
        },
        // The person's latest mobile code has no id: a new code replaces the old one outright (T4.12).
        IpPerson: { fields: { mobileCode: { merge: false } } },
      },
    }),
    clientAwareness: {
      name: 'frontend',
      version: '1.0.0',
    },
  })
}

// ========================================================
// Apollo client Instantiation
// ========================================================

let clientInstance: ApolloClient | null = null

export const getClientInstance = () => clientInstance

const originalGetClient = getClient
const getClientCached = (url?: string) => {
  clientInstance = originalGetClient(url)
  return clientInstance
}

export { getClientCached as getClient }
